import { type ChatMessageItem } from '../../components/chat/ChatMessage';
import { type SuggestedAction } from '../../types/ai';
import { type PreparedChatImage } from '../../utils/chat-image';

import { createId, getVisibleSuggestedActionLabel, pickSuggestedActionFields } from './constants';

const OUTFIT_BUILD_PATTERN =
  /\b(bygg|skapa|gör|fixa|visa)\b.*\b(outfit|look|uppsättning|kombination|stil)\b|\b(outfit|look)\b.*\b(för|till|på)\b/i;

const PRODUCT_SEARCH_PATTERN =
  /\b(visa|hitta|sök|leta|jag\s+vill\s+ha|jag\s+söker)\b.*\b(kavaj\w*|skjort\w*|sneaker\w*|byx\w*|tröj\w*|jack\w*|sko\w*|bälte\w*|accessoar\w*|chinos|kostym\w*|t-shirt|polo\w*)\b/i;

export const looksLikeOutfitBuild = (text: string): boolean => OUTFIT_BUILD_PATTERN.test(text);

export const looksLikeProductSearch = (text: string, hasImage: boolean): boolean =>
  !looksLikeOutfitBuild(text) && (hasImage || PRODUCT_SEARCH_PATTERN.test(text));

export type TurnMessages = {
  trimmed: string;
  structuredSuggestion?: SuggestedAction;
  userMessage: ChatMessageItem;
  assistantMessage: ChatMessageItem;
};

// Ger null när turen inte ska skickas alls — tomt fält utan bifogad bild.
export const buildTurnMessages = ({
  input,
  language,
  image,
  suggestedAction,
}: {
  input: string;
  language: string;
  image?: PreparedChatImage | null;
  suggestedAction?: SuggestedAction;
}): TurnMessages | null => {
  const trimmed = input.trim();
  if (!trimmed && !image) {
    return null;
  }

  const structuredSuggestion = pickSuggestedActionFields(suggestedAction);
  const createdAt = new Date().toISOString();

  const userMessage: ChatMessageItem = {
    id: createId(),
    role: 'user',
    content: getVisibleSuggestedActionLabel(structuredSuggestion) ?? trimmed,
    ...(image ? { imageUrl: image.dataUrl } : {}),
    createdAt,
    status: 'success',
  };

  const assistantMessage: ChatMessageItem = {
    id: createId(),
    role: 'assistant',
    content: '',
    createdAt,
    status: 'success',
    isOutfitBuilding: looksLikeOutfitBuild(trimmed),
    isProductSearching: looksLikeProductSearch(trimmed, Boolean(image)),
    detectedLanguage: language,
  };

  return { trimmed, structuredSuggestion, userMessage, assistantMessage };
};
